import React, { useEffect } from "react";
import { useState } from "react";

/** Whether the page has scrolled past a threshold (sticky header). */
export function useScrolled(threshold = 12) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

/** Loads a template's CDN fonts (idempotent per URL). */
function useFontLinks(urls = []) {
  useEffect(() => {
    const created = [];
    urls.forEach((href) => {
      if (document.querySelector(`link[data-tpl-font="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.setAttribute("data-tpl-font", href);
      document.head.appendChild(link);
      created.push(link);
    });
    // Fonts are left in place (reusable) — no cleanup.
  }, [urls]);
}

/**
 * The OPAQUE light surfaces the house pages paint content on (tailwind.config.js `eh.*`,
 * plus plain white). These are Classic-era utilities: an interior page writes
 * `bg-white rounded-2xl …` for a card and states no ink, because under Classic the ink it
 * inherits — `text-eh-ink` on Classic's `<main>` — is already the right one.
 *
 * ONLY OPAQUE ONES, ON PURPOSE. A translucent veil (`bg-white/20`, `bg-eh-cream/40`) is not
 * a light surface: its rendered colour is whatever it sits on, which on a dark theme is
 * dark. A theme cannot state the ink for a ground it cannot see, so those are left alone.
 */
const LIGHT_SURFACE_CLASSES = [
  "bg-white",
  "bg-eh-surface",
  "bg-eh-cream",
  "bg-eh-cream-deep",
  "bg-eh-bg",
  "bg-eh-bg-soft",
  "bg-eh-sage-soft",
];

/**
 * `[class~="x"]` matches the WHOLE class token, so `bg-white` is matched and `hover:bg-white`
 * / `md:bg-white` / `bg-white/20` are not — a conditional or translucent fill must not get an
 * unconditional ink.
 */
const surfaceSelectors = (ancestor, prefix = "") =>
  LIGHT_SURFACE_CLASSES.map((cls) => `${ancestor} ${prefix}[class~="${cls}"]`).join(",");

/**
 * TemplateShell — wraps a template: injects its fonts and applies its CSS variables
 * (colours/fonts), honouring the tenant's branding overrides (primary/accent/secondary).
 * bg/text stay controlled by the template to preserve legibility (e.g. dark templates).
 *
 * BUG-065 — TWO INKS, ONE OF WHICH THE THEME DOES NOT OWN THE GROUND FOR.
 * The rule below (`color: var(--tpl-ink-on-light)`) exists because the rule above it
 * (`color: var(--tpl-text)` on the wrapper) is inherited by everything the interior pages
 * render, including the white and cream cards they paint their content on. On the two dark
 * themes `--tpl-text` is near-white, so the course player's lesson list, the lesson title and
 * the lesson body — none of which state a colour of their own — became white on white.
 * Fixing it card by card would be N pages × M cards and would re-open on the next card
 * written; the ink belongs to the theme, so the theme states it, once, here.
 *
 * `:where()` IS LOAD-BEARING: it contributes ZERO specificity, so the rule is a DEFAULT.
 * Any explicit text utility on the same element (`text-eh-muted bg-eh-cream …`, a real
 * pattern in public/Boutique.jsx and public/JournalIndex.jsx) still wins, and a child's own
 * colour always wins. The rule only ever replaces an INHERITED value — never an author's.
 *
 * FORM CONTROLS NEED THEIR OWN RULE. Tailwind's preflight declares
 * `input,textarea,select { color: inherit }` at specificity (0,0,1), which outranks a
 * `:where()` default, so a `bg-white` field would keep inheriting the theme's near-white ink
 * (public/JournalPost.jsx's comment form, public/Boutique.jsx's order form). That rule is
 * therefore written at the LOWEST specificity that beats preflight, (0,1,1) — which does
 * outrank a `text-*` utility on the control itself. No public form control declares one
 * today; one added on a light ground would be overridden here, and that is the trade.
 */
export const TemplateShell = ({ template, branding = {}, children }) => {
  useFontLinks(template.fontUrls);
  const c = template.colors;
  const headFallback = template.id === "t6_luxe" || template.id === "t1_classic" ? "Georgia, serif" : "sans-serif";
  const vars = {
    "--tpl-bg": c.background,
    "--tpl-text": c.text,
    // The theme's ink for a light surface it does not own. Equal to `text` on the light
    // themes, which makes the rule below a no-op there rather than a second behaviour.
    "--tpl-ink-on-light": c.inkOnLight,
    "--tpl-primary": branding.primary_color || c.primary,
    "--tpl-secondary": branding.secondary_color || c.secondary,
    "--tpl-accent": branding.accent_color || c.accent,
    "--tpl-font-head": `'${template.fonts.heading}', ${headFallback}`,
    "--tpl-font-body": `'${template.fonts.body}', sans-serif`,
  };
  return (
    <div data-tpl={template.id} data-testid={`template-${template.id}`} style={vars}>
      <style>{`
        [data-tpl="${template.id}"]{font-family:var(--tpl-font-body);color:var(--tpl-text);background:var(--tpl-bg);}
        [data-tpl="${template.id}"] .tpl-head{font-family:var(--tpl-font-head);}
        [data-tpl="${template.id}"] ::selection{background:var(--tpl-accent);color:var(--tpl-bg);}
        :where(${surfaceSelectors(`[data-tpl="${template.id}"]`)}){color:var(--tpl-ink-on-light);}
        ${surfaceSelectors(`:where([data-tpl="${template.id}"])`, ":is(input,textarea,select)")}{color:var(--tpl-ink-on-light);}
      `}</style>
      {children}
    </div>
  );
};

/** Compact language switch, reusable by the templates. */
export const LangSwitch = ({ lang, setLang, className = "", activeClass = "", idleClass = "" }) => (
  <div className={`flex items-center gap-1 text-xs font-semibold ${className}`} data-testid="tpl-lang-switch">
    {["fr", "en", "es"].map((l) => (
      <button
        key={l}
        onClick={() => setLang(l)}
        data-testid={`tpl-lang-${l}`}
        className={`px-2 py-1 rounded-full uppercase transition ${lang === l ? activeClass : idleClass}`}
      >
        {l}
      </button>
    ))}
  </div>
);

export default TemplateShell;
